import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Monitor, Zap, Clock, CheckCircle2, AlertCircle, Coffee, Film, Image, Layers, Video, Sparkles, LayoutGrid } from 'lucide-react';

import API from '../utils/api';

const TYPE_ICONS = {
  'Short-form Video': <Film size={18} className="text-pink-400" />,
  'Long-form Video': <Video size={18} className="text-purple-400" />,
  'Static': <Image size={18} className="text-blue-400" />,
  'Carousel': <Layers size={18} className="text-amber-400" />,
  'Motion Graphic': <Sparkles size={18} className="text-cyan-400" />,
  'Grid': <LayoutGrid size={18} className="text-teal-400" />,
};

function CastClock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const iv = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  const hours = time.getHours().toString().padStart(2, '0');
  const mins = time.getMinutes().toString().padStart(2, '0');
  const secs = time.getSeconds().toString().padStart(2, '0');
  const dateStr = time.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="text-center py-6" data-testid="cast-clock">
      <div className="text-[8rem] md:text-[10rem] font-black tracking-tighter leading-none text-white font-mono">
        {hours}<span className="text-indigo-500 animate-pulse">:</span>{mins}<span className="text-indigo-500 animate-pulse">:</span><span className="text-white/40">{secs}</span>
      </div>
      <p className="text-white/30 text-lg tracking-[0.3em] uppercase mt-2">{dateStr}</p>
    </div>
  );
}

function EmployeeRow({ emp, tick }) {
  const isWorking = emp.is_working && emp.current_task;

  const formatHMS = (s) => {
    if (s <= 0) return '00:00:00';
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = Math.floor(s % 60);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  // Started at = first session start time
  const startedAt = isWorking && emp.current_task.started_at
    ? new Date(emp.current_task.started_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
    : '—';

  // Total elapsed: previous_secs (completed sessions) + live current session
  const totalElapsed = (() => {
    if (!isWorking) return '—';
    const ct = emp.current_task;
    const prevSecs = ct.previous_secs || 0;
    if (ct.running_since) {
      const runStart = new Date(ct.running_since).getTime();
      const currentSecs = Math.floor((Date.now() - runStart) / 1000);
      return formatHMS(prevSecs + currentSecs);
    }
    return formatHMS(prevSecs);
  })();

  return (
    <div
      className={`grid grid-cols-12 items-center px-6 py-4 border-b border-white/5 transition-colors ${isWorking ? 'bg-emerald-500/5' : 'bg-white/[0.01]'}`}
      data-testid={`cast-emp-${emp.employee_id}`}
    >
      {/* Status indicator + Name */}
      <div className="col-span-2 flex items-center gap-4">
        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${isWorking ? 'bg-emerald-500 animate-pulse shadow-[0_0_12px_rgba(16,185,129,0.6)]' : 'bg-white/10'}`} />
        <div>
          <div className="text-white text-xl font-bold tracking-tight">{emp.name}</div>
          <div className="text-white/20 text-xs uppercase tracking-widest">{emp.department}</div>
        </div>
      </div>

      {/* Current Task */}
      <div className="col-span-3 flex items-center gap-3">
        {isWorking ? (
          <>
            {TYPE_ICONS[emp.current_task.task_type] || <Zap size={18} className="text-white/30" />}
            <div>
              <div className="text-white/90 text-base font-medium">{emp.current_task.task_type}</div>
              <div className="text-white/30 text-sm">{emp.current_task.client_name}</div>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2 text-white/20">
            <Coffee size={18} />
            <span className="text-sm">No active task</span>
          </div>
        )}
      </div>

      {/* Started At */}
      <div className="col-span-2 text-center">
        <div className="text-white/60 text-lg font-mono">{startedAt}</div>
      </div>

      {/* Total Time Elapsed */}
      <div className="col-span-3 text-center">
        {isWorking ? (
          <div className="font-mono text-3xl font-black text-emerald-400 tracking-wider" data-testid={`cast-timer-${emp.employee_id}`}>
            {totalElapsed}
          </div>
        ) : (
          <span className="text-white/10 text-lg">—</span>
        )}
      </div>

      {/* Stats */}
      <div className="col-span-2 flex items-center justify-end gap-5">
        <div className="text-center">
          <div className="text-amber-400 text-2xl font-black font-mono">{emp.pending}</div>
          <div className="text-white/20 text-[10px] uppercase tracking-widest">Pending</div>
        </div>
        <div className="text-center">
          <div className="text-emerald-400 text-2xl font-black font-mono">{emp.completed}</div>
          <div className="text-white/20 text-[10px] uppercase tracking-widest">Done</div>
        </div>
      </div>
    </div>
  );
}

export default function ProductionCastPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [tick, setTick] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/production/cast-data`, { withCredentials: true });
      setData(res.data);
      setError(false);
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Refresh data every 10 seconds
  useEffect(() => {
    const iv = setInterval(fetchData, 10000);
    return () => clearInterval(iv);
  }, [fetchData]);

  // Tick every second to update live timers
  useEffect(() => {
    const iv = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(iv);
  }, []);

  if (error && !data) {
    return (
      <div className="min-h-screen bg-[#030306] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={48} className="text-rose-500 mx-auto mb-4" />
          <p className="text-white/50 text-lg">Unable to load cast data. Please ensure you are logged in.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#030306] flex items-center justify-center">
        <div className="text-white/30 text-2xl animate-pulse">Loading Cast Mode...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030306] text-white overflow-hidden" data-testid="cast-mode-page">
      {/* Header bar */}
      <div className="flex items-center justify-between px-8 pt-4">
        <div className="flex items-center gap-3">
          <Monitor size={20} className="text-indigo-500" />
          <span className="text-white/30 text-xs uppercase tracking-[0.4em] font-semibold">Scale Sense HQ — Live Production</span>
        </div>
        <div className="flex items-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <Zap size={14} className="text-emerald-400" />
            <span className="text-emerald-400 font-bold text-lg">{data.total_live}</span>
            <span className="text-white/20 text-xs uppercase">Live</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-amber-400" />
            <span className="text-amber-400 font-bold text-lg">{data.total_pending}</span>
            <span className="text-white/20 text-xs uppercase">Pending</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 size={14} className="text-cyan-400" />
            <span className="text-cyan-400 font-bold text-lg">{data.total_completed}</span>
            <span className="text-white/20 text-xs uppercase">Completed</span>
          </div>
        </div>
      </div>

      {/* Big Clock */}
      <CastClock />

      {/* Employee Grid */}
      <div className="mx-6 rounded-xl border border-white/5 overflow-hidden bg-white/[0.02]" data-testid="cast-employees-table">
        {/* Header */}
        <div className="grid grid-cols-12 items-center px-6 py-3 bg-white/[0.03] border-b border-white/10">
          <div className="col-span-2 text-white/30 text-xs uppercase tracking-[0.2em] font-semibold">Employee</div>
          <div className="col-span-3 text-white/30 text-xs uppercase tracking-[0.2em] font-semibold">Working On</div>
          <div className="col-span-2 text-white/30 text-xs uppercase tracking-[0.2em] font-semibold text-center">Started At</div>
          <div className="col-span-3 text-white/30 text-xs uppercase tracking-[0.2em] font-semibold text-center">Total Time</div>
          <div className="col-span-2 text-white/30 text-xs uppercase tracking-[0.2em] font-semibold text-right pr-4">Tasks</div>
        </div>

        {/* Rows */}
        {data.employees.length === 0 ? (
          <div className="py-16 text-center text-white/20 text-lg">No production team members found</div>
        ) : (
          data.employees.map(emp => <EmployeeRow key={emp.employee_id} emp={emp} tick={tick} />)
        )}
      </div>

      {/* Footer */}
      <div className="text-center py-4 text-white/10 text-xs tracking-widest uppercase">
        Auto-refreshes every 10 seconds
      </div>
    </div>
  );
}
